import { Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { TimeOfDayType } from './page-gradient.config';
import { TimeOfDayService } from './time-of-day.service';

@Directive({
  selector: '[appGradientPage]',
  standalone: true,
})
export class GradientPageDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private currentTimeOfDay: TimeOfDayType;
  private activeClone: HTMLElement | null = null;
  private transitionTimeoutId: number | null = null;

  private gradientsByDayTime = {
    night: 'radial-gradient(50.00% 39.72% at 50.00% 100.00%, #B8D6F2 0%, rgba(184, 214, 242, 0.00) 100%), radial-gradient(106.96% 100.00% at 50.00% 100.00%, #84BDE7 0%, rgba(179, 193, 241, 0.00) 100%, rgba(241, 183, 179, 0.00) 100%, rgba(132, 189, 231, 0.00) 100%), radial-gradient(100.00% 100% at 50.00% 100.00%, rgba(11, 54, 104, 0.00) 0%, #0B3668 100%), #8ABEFB',
    morning: 'radial-gradient(50.00% 39.72% at 50.00% 100.00%, rgba(255, 247, 239, 0.80) 0%, rgba(255, 242, 231, 0.00) 100%), radial-gradient(106.96% 100.00% at 50.00% 100.00%, #F1B7B3 0%, rgba(240, 179, 241, 0.00) 100%, rgba(241, 183, 179, 0.00) 100%), radial-gradient(100.00% 100% at 50.00% 100.00%, rgba(216, 234, 255, 0.00) 0%, #8098BC 100%), #BBD2EC',
    day: 'radial-gradient(62.94% 50% at 50.00% 0.00%, #FFF 0%, #FFFBEB 0.01%, rgba(255, 251, 235, 0.00) 100%), radial-gradient(106.96% 100.00% at 50.00% 0.00%, #FFF3C8 0%, rgba(240, 179, 241, 0.00) 100%, rgba(255, 243, 200, 0.00) 100%), radial-gradient(100.00% 100% at 50.00% 100.00%, rgba(209, 244, 255, 0.00) 0%, #D1F4FF 100%), #B5DBF1',
    afternoon: 'radial-gradient(50.00% 39.72% at 50.00% 100.00%, #FFE5C7 0%, rgba(255, 216, 199, 0.00) 100%), radial-gradient(106.96% 100.00% at 50.00% 100.00%, #FF8D7D 0%, rgba(240, 179, 241, 0.00) 100%, rgba(255, 91, 69, 0.00) 100%, rgba(255, 141, 125, 0.00) 100%), radial-gradient(100.00% 100% at 50.00% 100.00%, rgba(233, 157, 140, 0.00) 0%, #A2A0F6 100%), #D59BD6',
    evening: 'radial-gradient(50.00% 39.72% at 50.00% 100.00%, #B8D6F2 0%, rgba(184, 214, 242, 0.00) 100%), radial-gradient(106.96% 100.00% at 50.00% 100.00%, #84BDE7 0%, rgba(179, 193, 241, 0.00) 100%, rgba(241, 183, 179, 0.00) 100%, rgba(132, 189, 231, 0.00) 100%), radial-gradient(100.00% 100% at 50.00% 100.00%, rgba(11, 54, 104, 0.00) 0%, #0B3668 100%), #8ABEFB',
  };

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private router: Router,
    private timeOfDayService: TimeOfDayService
  ) {
    this.currentTimeOfDay = this.timeOfDayService.currentTimeOfDay;
  }
  
  ngOnInit(): void {
    this.applyGradient(this.currentTimeOfDay);
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.applyGradient(this.currentTimeOfDay));

    this.timeOfDayService.changed$
      .pipe(takeUntil(this.destroy$))
      .subscribe((timeOfDay) => {
        if (timeOfDay === this.currentTimeOfDay) {
          return;
        }
        this.transitionGradient(timeOfDay);
        this.currentTimeOfDay = timeOfDay;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupTransition();
  }

  private applyGradient(timeOfDay: TimeOfDayType): void {
    this.renderer.setStyle(
      this.elRef.nativeElement,
      'background',
      this.gradientsByDayTime[timeOfDay]
    );
  }

  private transitionGradient(nextTimeOfDay: TimeOfDayType): void {
    const host = this.elRef.nativeElement;
    const parent = host.parentElement;
    if (!parent) {
      this.applyGradient(nextTimeOfDay);
      return;
    }

    this.cleanupTransition();

    const clone = host.cloneNode(false) as HTMLElement;
    this.renderer.setStyle(clone, 'background', this.gradientsByDayTime[nextTimeOfDay]);
    this.renderer.setStyle(clone, 'opacity', '0');
    this.renderer.setStyle(clone, 'transition', 'opacity 2s ease');
    this.renderer.setStyle(clone, 'pointer-events', 'none');
    this.renderer.setStyle(clone, 'z-index', '-1');

    this.renderer.appendChild(parent, clone);
    this.activeClone = clone;

    requestAnimationFrame(() => {
      if (this.activeClone === clone) {
        this.renderer.setStyle(clone, 'opacity', '1');
      }
    });

    this.transitionTimeoutId = window.setTimeout(() => {
      this.applyGradient(nextTimeOfDay);
      this.cleanupTransition();
    }, 2000);
  }

  private cleanupTransition(): void {
    if (this.transitionTimeoutId !== null) {
      window.clearTimeout(this.transitionTimeoutId);
      this.transitionTimeoutId = null;
    }

    if (this.activeClone) {
      const parent = this.activeClone.parentElement;
      if (parent) {
        this.renderer.removeChild(parent, this.activeClone);
      }
      this.activeClone = null;
    }
  }
}
